import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X, Printer, FileSpreadsheet, BarChart3, Building2, FolderKanban,
  UserCheck, ShieldCheck, TrendingUp, Clock, CheckCircle2, FileText,
  Loader2, Calendar, DollarSign, Package, ClipboardList
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'analytics', label: 'لوحة تحليلية', icon: BarChart3 },
  { id: 'sheets', label: 'قائمة الكشوف', icon: ClipboardList },
  { id: 'creator', label: 'حسب المدخل', icon: UserCheck },
  { id: 'confirmer', label: 'حسب المعتمِد', icon: ShieldCheck },
  { id: 'supplier', label: 'حسب المورد', icon: Building2 },
  { id: 'project', label: 'حسب المشروع', icon: FolderKanban },
];

const STATUS_COLORS = { confirmed: '#7c3aed', draft: '#a78bfa', cancelled: '#94a3b8' };

const fmtNum = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

// ===================================================================
// لوحة الفلترة
function FiltersPanel({ filters, setFilters, options, onClear, activeCount }) {
  const update = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  return (
    <div className="bg-purple-50/50 border border-purple-200 rounded-xl p-3 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="matching-reports-filters">
      <div>
        <label className="block text-xs text-gray-600 mb-1">المورد</label>
        <select value={filters.supplier_name} onChange={(e) => update('supplier_name', e.target.value)} data-testid="mf-supplier" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل الموردين</option>
          {(options.suppliers || []).map((s, i) => <option key={i} value={s.name}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">المشروع</label>
        <select value={filters.project_number} onChange={(e) => update('project_number', e.target.value)} data-testid="mf-project" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل المشاريع</option>
          {(options.projects || []).map((p, i) => <option key={i} value={p.number}>{p.number} - {p.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">مدخل الكشف</label>
        <select value={filters.creator_id} onChange={(e) => update('creator_id', e.target.value)} data-testid="mf-creator" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل المدخلين</option>
          {(options.creators || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">المعتمِد</label>
        <select value={filters.confirmer_id} onChange={(e) => update('confirmer_id', e.target.value)} data-testid="mf-confirmer" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل المعتمِدين</option>
          {(options.confirmers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">الحالة</label>
        <select value={filters.status} onChange={(e) => update('status', e.target.value)} data-testid="mf-status" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل الحالات</option>
          <option value="draft">مسودة</option>
          <option value="confirmed">مؤكَّد</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">تاريخ الإدخال من</label>
        <input type="date" value={filters.date_from} onChange={(e) => update('date_from', e.target.value)} data-testid="mf-date-from" className="w-full h-10 px-2 border rounded-lg bg-white" />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">تاريخ الإدخال إلى</label>
        <input type="date" value={filters.date_to} onChange={(e) => update('date_to', e.target.value)} data-testid="mf-date-to" className="w-full h-10 px-2 border rounded-lg bg-white" />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">تاريخ التأكيد من</label>
        <input type="date" value={filters.confirm_date_from} onChange={(e) => update('confirm_date_from', e.target.value)} data-testid="mf-confirm-date-from" className="w-full h-10 px-2 border rounded-lg bg-white" />
      </div>
      <div className="lg:col-span-4 sm:col-span-2 flex justify-between items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs text-gray-600 mb-1">تاريخ التأكيد إلى</label>
          <input type="date" value={filters.confirm_date_to} onChange={(e) => update('confirm_date_to', e.target.value)} data-testid="mf-confirm-date-to" className="w-full h-10 px-2 border rounded-lg bg-white" />
        </div>
        {activeCount > 0 && (
          <button onClick={onClear} data-testid="mf-clear" className="h-10 px-4 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1 font-medium whitespace-nowrap">
            <X className="w-4 h-4" /> مسح ({activeCount})
          </button>
        )}
      </div>
    </div>
  );
}

// ===================================================================
function Kpi({ label, value, icon: Icon, color = 'purple', suffix = '' }) {
  const colors = {
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    fuchsia: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  };
  return (
    <div className={`rounded-xl p-3 border-2 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-1">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}{suffix && <span className="text-xs mr-1 opacity-70">{suffix}</span>}</div>
    </div>
  );
}

function ChipsList({ filters, options }) {
  const chips = [];
  const findById = (arr, id) => (arr || []).find((x) => x.id === id)?.name;
  if (filters.supplier_name) chips.push(['المورد', filters.supplier_name]);
  if (filters.project_number) {
    const p = (options.projects || []).find((x) => x.number === filters.project_number);
    chips.push(['المشروع', p ? `${p.number} - ${p.name}` : filters.project_number]);
  }
  if (filters.creator_id) chips.push(['المدخل', findById(options.creators, filters.creator_id) || filters.creator_id]);
  if (filters.confirmer_id) chips.push(['المعتمِد', findById(options.confirmers, filters.confirmer_id) || filters.confirmer_id]);
  if (filters.status) chips.push(['الحالة', filters.status === 'draft' ? 'مسودة' : 'مؤكَّد']);
  if (filters.date_from) chips.push(['إدخال من', filters.date_from]);
  if (filters.date_to) chips.push(['إدخال إلى', filters.date_to]);
  if (filters.confirm_date_from) chips.push(['تأكيد من', filters.confirm_date_from]);
  if (filters.confirm_date_to) chips.push(['تأكيد إلى', filters.confirm_date_to]);
  if (!chips.length) return <span className="text-xs text-gray-500">لم يتم تطبيق أي فلتر</span>;
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      {chips.map(([k, v], i) => (
        <span key={i} className="bg-white border border-purple-300 text-purple-800 px-2 py-1 rounded-full">
          <strong>{k}:</strong> {v}
        </span>
      ))}
    </div>
  );
}

// ===================================================================
// التبويب التحليلي
function AnalyticsTab({ data }) {
  const k = data?.kpis || {};
  const pieData = [
    { name: 'مؤكَّد', value: k.confirmed || 0, color: STATUS_COLORS.confirmed },
    { name: 'مسودة', value: k.draft || 0, color: STATUS_COLORS.draft },
  ].filter((p) => p.value > 0);
  
  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <Kpi label="إجمالي الكشوف" value={fmtNum(k.total_sheets)} icon={ClipboardList} color="purple" />
        <Kpi label="مؤكَّد" value={fmtNum(k.confirmed)} icon={CheckCircle2} color="green" />
        <Kpi label="مسودة" value={fmtNum(k.draft)} icon={FileText} color="amber" />
        <Kpi label="إجمالي الفواتير" value={fmtNum(k.total_invoices)} icon={Package} color="blue" />
        <Kpi label="إجمالي المبالغ" value={fmtNum(k.total_amount)} icon={DollarSign} color="fuchsia" suffix="ر.س" />
        <Kpi label="نسبة التأكيد" value={k.confirmation_rate || 0} suffix="%" icon={TrendingUp} color="indigo" />
        <Kpi label="متوسط التأكيد" value={k.avg_confirm_days || 0} suffix="يوم" icon={Clock} color="rose" />
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-700"><Calendar className="w-4 h-4" /> الإدخالات والمبالغ بمرور الوقت</h3>
          {data?.timeline?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.timeline}>
                <defs>
                  <linearGradient id="amtGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.7}/>
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="amount" stroke="#a855f7" fill="url(#amtGrad)" name="مبلغ" />
                <Line type="monotone" dataKey="invoices" stroke="#2563eb" strokeWidth={2} name="فواتير" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-12 text-sm">لا توجد بيانات</p>}
        </div>
        
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-700"><BarChart3 className="w-4 h-4" /> توزيع الكشوف</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={(e) => `${e.name}: ${e.value}`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-12 text-sm">لا توجد بيانات</p>}
        </div>
      </div>
      
      {/* Top lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TopList title="أعلى 5 موردين" icon={Building2} items={data?.top_suppliers} labelKey="supplier_name" valueKey="invoices" amountKey="amount" />
        <TopList title="أعلى 5 مشاريع" icon={FolderKanban} items={data?.top_projects} labelKey="project_name" subKey="project_number" valueKey="invoices" amountKey="amount" />
        <TopList title="أعلى 5 مدخلين" icon={UserCheck} items={data?.top_creators} labelKey="creator_name" valueKey="sheets" amountKey="amount" />
      </div>
    </div>
  );
}

function TopList({ title, icon: Icon, items = [], labelKey, subKey, valueKey, amountKey }) {
  const ranks = ['bg-yellow-400', 'bg-gray-300', 'bg-orange-400', 'bg-blue-300', 'bg-purple-300'];
  return (
    <div className="bg-white border rounded-xl p-4">
      <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-700"><Icon className="w-4 h-4" /> {title}</h3>
      {items?.length ? (
        <ol className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className={`w-6 h-6 ${ranks[i] || 'bg-gray-200'} text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{it[labelKey] || '—'}</div>
                {subKey && it[subKey] && <div className="text-xs text-gray-500">{it[subKey]}</div>}
              </div>
              <div className="text-left shrink-0">
                <div className="text-purple-700 font-bold">{it[valueKey]}</div>
                {amountKey && it[amountKey] > 0 && <div className="text-xs text-gray-500">{fmtNum(it[amountKey])}</div>}
              </div>
            </li>
          ))}
        </ol>
      ) : <p className="text-center text-gray-400 py-6 text-sm">لا توجد بيانات</p>}
    </div>
  );
}

// ===================================================================
// تبويب جدولي
function TableTab({ tab, data }) {
  const items = data?.report || data?.sheets || [];
  if (!items.length) {
    return <div className="text-center py-12 text-gray-400">لا توجد بيانات مطابقة للفلاتر</div>;
  }
  
  if (tab === 'sheets') {
    return (
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-purple-50">
            <tr>
              <th className="text-right px-3 py-2">كشف #</th>
              <th className="text-right px-3 py-2">تاريخ</th>
              <th className="text-right px-3 py-2">الحالة</th>
              <th className="text-right px-3 py-2">فواتير</th>
              <th className="text-right px-3 py-2">موردين</th>
              <th className="text-right px-3 py-2">المبلغ</th>
              <th className="text-right px-3 py-2">المدخل</th>
              <th className="text-right px-3 py-2">المعتمِد</th>
              <th className="text-right px-3 py-2">تاريخ التأكيد</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-bold">#{s.sheet_number}</td>
                <td className="px-3 py-2">{s.sheet_date}</td>
                <td className="px-3 py-2">
                  {s.status === 'confirmed' ? (
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-semibold">مؤكَّد</span>
                  ) : (
                    <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-semibold">مسودة</span>
                  )}
                </td>
                <td className="px-3 py-2 text-blue-700 font-bold">{s.invoices_count}</td>
                <td className="px-3 py-2 text-gray-700">{s.suppliers_count}</td>
                <td className="px-3 py-2 text-fuchsia-700 font-semibold">{fmtNum(s.total_amount)}</td>
                <td className="px-3 py-2 text-purple-700">{s.created_by || '—'}</td>
                <td className="px-3 py-2 text-indigo-700">{s.confirmed_by || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{fmtDate(s.confirmed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  
  if (tab === 'creator') {
    return (
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-green-50">
            <tr>
              <th className="text-right px-3 py-2">#</th>
              <th className="text-right px-3 py-2">المدخل</th>
              <th className="text-right px-3 py-2">كشوف</th>
              <th className="text-right px-3 py-2">مؤكَّد</th>
              <th className="text-right px-3 py-2">مسودة</th>
              <th className="text-right px-3 py-2">فواتير</th>
              <th className="text-right px-3 py-2">المبلغ</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                <td className="px-3 py-2 font-semibold">{it.creator_name || '—'}</td>
                <td className="px-3 py-2 text-gray-900 font-bold">{it.total_sheets}</td>
                <td className="px-3 py-2 text-green-700">{it.confirmed_count}</td>
                <td className="px-3 py-2 text-amber-700">{it.draft_count}</td>
                <td className="px-3 py-2 text-blue-700">{it.total_invoices}</td>
                <td className="px-3 py-2 text-fuchsia-700 font-semibold">{fmtNum(it.total_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  
  if (tab === 'confirmer') {
    return (
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-indigo-50">
            <tr>
              <th className="text-right px-3 py-2">#</th>
              <th className="text-right px-3 py-2">المعتمِد</th>
              <th className="text-right px-3 py-2">كشوف مؤكَّدة</th>
              <th className="text-right px-3 py-2">فواتير مؤكَّدة</th>
              <th className="text-right px-3 py-2">المبلغ</th>
              <th className="text-right px-3 py-2">متوسط وقت التأكيد (يوم)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                <td className="px-3 py-2 font-semibold">{it.confirmer_name || '—'}</td>
                <td className="px-3 py-2 text-indigo-700 font-bold">{it.total_confirmed_sheets}</td>
                <td className="px-3 py-2 text-blue-700 font-bold">{it.total_confirmed_invoices}</td>
                <td className="px-3 py-2 text-fuchsia-700 font-semibold">{fmtNum(it.total_amount)}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${it.avg_confirm_days <= 1 ? 'bg-green-100 text-green-700' : it.avg_confirm_days <= 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{it.avg_confirm_days ?? 0}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  
  // supplier or project
  const isSupplier = tab === 'supplier';
  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-3">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={items.slice(0, 10)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey={isSupplier ? 'supplier_name' : 'project_number'} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="total_invoices" fill="#7c3aed" name="فواتير" />
            <Bar dataKey="total_sheets" fill="#a78bfa" name="كشوف" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className={isSupplier ? 'bg-purple-50' : 'bg-blue-50'}>
            <tr>
              <th className="text-right px-3 py-2">#</th>
              <th className="text-right px-3 py-2">{isSupplier ? 'المورد' : 'رقم المشروع'}</th>
              {!isSupplier && <th className="text-right px-3 py-2">اسم المشروع</th>}
              <th className="text-right px-3 py-2">كشوف</th>
              <th className="text-right px-3 py-2">فواتير</th>
              <th className="text-right px-3 py-2">المبلغ</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                <td className="px-3 py-2 font-semibold">{isSupplier ? it.supplier_name : it.project_number}</td>
                {!isSupplier && <td className="px-3 py-2 text-gray-600">{it.project_name}</td>}
                <td className="px-3 py-2 text-purple-700 font-bold">{it.total_sheets}</td>
                <td className="px-3 py-2 text-blue-700 font-bold">{it.total_invoices}</td>
                <td className="px-3 py-2 text-fuchsia-700 font-semibold">{fmtNum(it.total_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================================================================
// تصدير CSV
function exportToCsv(tab, data) {
  let headers = [], rows = [];
  if (tab === 'sheets') {
    headers = ['#', 'رقم الكشف', 'التاريخ', 'الحالة', 'فواتير', 'موردين', 'المبلغ', 'المدخل', 'المعتمِد', 'تاريخ التأكيد'];
    rows = (data?.sheets || []).map((s, i) => [i + 1, s.sheet_number, s.sheet_date, s.status === 'confirmed' ? 'مؤكَّد' : 'مسودة', s.invoices_count, s.suppliers_count, s.total_amount, s.created_by, s.confirmed_by || '', fmtDate(s.confirmed_at)]);
  } else if (tab === 'creator') {
    headers = ['#', 'المدخل', 'كشوف', 'مؤكَّد', 'مسودة', 'فواتير', 'المبلغ'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.creator_name, it.total_sheets, it.confirmed_count, it.draft_count, it.total_invoices, it.total_amount]);
  } else if (tab === 'confirmer') {
    headers = ['#', 'المعتمِد', 'كشوف مؤكَّدة', 'فواتير', 'المبلغ', 'متوسط وقت التأكيد (يوم)'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.confirmer_name, it.total_confirmed_sheets, it.total_confirmed_invoices, it.total_amount, it.avg_confirm_days ?? 0]);
  } else if (tab === 'supplier') {
    headers = ['#', 'المورد', 'كشوف', 'فواتير', 'المبلغ'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.supplier_name, it.total_sheets, it.total_invoices, it.total_amount]);
  } else if (tab === 'project') {
    headers = ['#', 'رقم المشروع', 'اسم المشروع', 'كشوف', 'فواتير', 'المبلغ'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.project_number, it.project_name, it.total_sheets, it.total_invoices, it.total_amount]);
  } else if (tab === 'analytics') {
    headers = ['المؤشر', 'القيمة'];
    const k = data?.kpis || {};
    rows = [
      ['إجمالي الكشوف', k.total_sheets],
      ['مؤكَّد', k.confirmed],
      ['مسودة', k.draft],
      ['إجمالي الفواتير', k.total_invoices],
      ['إجمالي المبالغ', k.total_amount],
      ['نسبة التأكيد %', k.confirmation_rate],
      ['متوسط التأكيد (يوم)', k.avg_confirm_days],
    ];
  }
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `تقرير-مطابقة-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ===================================================================
// المكوّن الرئيسي
export function MatchingReportsView({ token }) {
  const [activeTab, setActiveTab] = useState('analytics');
  const [filters, setFilters] = useState({
    supplier_name: '', project_number: '', creator_id: '', confirmer_id: '',
    status: '', date_from: '', date_to: '', confirm_date_from: '', confirm_date_to: ''
  });
  const [options, setOptions] = useState({ suppliers: [], projects: [], creators: [], confirmers: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;
  const clearFilters = () => setFilters({
    supplier_name: '', project_number: '', creator_id: '', confirmer_id: '',
    status: '', date_from: '', date_to: '', confirm_date_from: '', confirm_date_to: ''
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  }, [filters]);

  useEffect(() => {
    fetch(`${API_URL}/api/invoice-matching/reports/filters-options`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : null).then(d => d && setOptions(d));
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const ep = activeTab === 'analytics' ? 'analytics'
        : activeTab === 'sheets' ? 'sheets-list'
        : activeTab === 'creator' ? 'by-creator'
        : activeTab === 'confirmer' ? 'by-confirmer'
        : activeTab === 'supplier' ? 'by-supplier'
        : 'by-project';
      const res = await fetch(`${API_URL}/api/invoice-matching/reports/${ep}?${queryString}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [activeTab, queryString, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeTabMeta = TABS.find(t => t.id === activeTab);

  if (printMode) {
    return <PrintView activeTab={activeTab} filters={filters} options={options} data={data} onClose={() => setPrintMode(false)} />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="matching-reports-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`mr-tab-${tab.id}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                active ? 'bg-purple-600 text-white shadow' : 'bg-white border text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
      
      <FiltersPanel filters={filters} setFilters={setFilters} options={options} onClear={clearFilters} activeCount={activeFiltersCount} />
      
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <activeTabMeta.icon className="w-4 h-4" />
          <span className="font-medium">{activeTabMeta.label}</span>
          {activeFiltersCount > 0 && (
            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs">{activeFiltersCount} فلتر</span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportToCsv(activeTab, data)}
            disabled={loading || !data}
            data-testid="mr-export-excel"
            className="h-10 px-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button
            onClick={() => setPrintMode(true)}
            disabled={loading || !data}
            data-testid="mr-print"
            className="h-10 px-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> طباعة / PDF
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>
      ) : activeTab === 'analytics' ? (
        <AnalyticsTab data={data} />
      ) : (
        <TableTab tab={activeTab} data={data} />
      )}
    </div>
  );
}

// ===================================================================
function PrintView({ activeTab, filters, options, data, onClose }) {
  const activeMeta = TABS.find(t => t.id === activeTab);
  const handlePrint = () => window.print();
  
  return (
    <div className="fixed inset-0 z-50 bg-white overflow-auto" dir="rtl" data-print-root>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          html, body { height: auto !important; overflow: visible !important; }
          body > *:not([data-print-root]) { display: none !important; }
          [data-print-root] { position: static !important; width: 100% !important; height: auto !important; }
          .print-hide { display: none !important; }
          .recharts-wrapper, .recharts-surface { overflow: visible !important; }
        }
      `}</style>
      <div className="print-hide bg-gray-50 border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-gray-200 flex items-center justify-center" data-testid="mr-print-close">
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 font-bold">معاينة طباعة: تقارير مطابقة الفواتير — {activeMeta?.label}</h2>
        <button onClick={handlePrint} data-testid="mr-print-action" className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-semibold">
          <Printer className="w-4 h-4" /> طباعة / حفظ PDF
        </button>
      </div>
      <div className="max-w-6xl mx-auto p-6 print:p-2">
        <div className="text-center mb-4">
          <h1 className="text-2xl print:text-xl font-bold">تقرير مطابقة الفواتير — {activeMeta?.label}</h1>
          <p className="text-sm text-gray-600 mt-1">تاريخ الطباعة: {new Date().toLocaleDateString('en-US')}</p>
        </div>
        
        <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 mb-4">
          <div className="text-xs font-bold text-purple-900 mb-2">الفلاتر المُطبَّقة:</div>
          <ChipsList filters={filters} options={options} />
        </div>
        
        {activeTab === 'analytics' ? <AnalyticsTab data={data} /> : <TableTab tab={activeTab} data={data} />}
      </div>
    </div>
  );
}
